import React, { useLayoutEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import { useToastStore } from '@/store';
import { downloadMedia } from '@/constants/utils/mediaDownload';
import { createFileOpenGuard } from '@/lib/fileOpenSession';

// prepareFile exports structured previews; binary files can be saved directly.
export default function ArtifactDownloadButton({ file, prepareFile, disabled = false, style }) {
  const { colors } = useTheme();
  const addToast = useToastStore(state => state.actions.addToast);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);
  const generation = useRef(0);
  const prepared = useRef(null);
  useLayoutEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useLayoutEffect(() => { generation.current += 1; prepared.current = null; }, [file, prepareFile]);
  const save = async () => {
    if (pending.current || disabled) return;
    const attempt = generation.current;
    const isCurrent = createFileOpenGuard(() => mounted.current && attempt === generation.current);
    if (!isCurrent()) return;
    pending.current = true;
    setBusy(true);
    try {
      const cached = prepared.current?.isCurrent?.() ? prepared.current.file : null;
      const downloadFile = cached || (prepareFile ? await prepareFile({ isRequestCurrent: isCurrent }) : file);
      // Closing/switching previews during an export must not open a stale Save dialog.
      if (!isCurrent()) return;
      const url = downloadFile?.cdnPath || downloadFile?.url || downloadFile?.uri;
      if (!url) { addToast('This file is not available to save.', 'error'); return; }
      // Reuse the exported copy if the Save dialog is cancelled or the disk is full.
      prepared.current = { file: downloadFile, isCurrent };
      const result = await downloadMedia(url, downloadFile?.name || downloadFile?.originalName || 'file', { shouldSave: isCurrent });
      if (!isCurrent() || result?.cancelled || result?.canceled) return;
      if (result?.success) addToast('Download complete', 'success');
      else addToast(result?.message || 'Could not save this file. Try again.', 'error');
    } catch {
      if (isCurrent()) addToast('Could not save this file. Try again.', 'error');
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return <Pressable accessibilityRole="button" accessibilityLabel="Save to Files"
    accessibilityState={{ busy, disabled: busy || disabled }} disabled={busy || disabled} onPress={save} style={style}>
    {busy ? <ActivityIndicator size="small" color={colors.text_color_primary} />
      : <Icon name="arrow-down-to-line" size={13} color={colors.text_color_primary} />}
    <Text size="s">{busy ? 'Saving…' : 'Save to Files'}</Text>
  </Pressable>;
}
